import React, { useEffect, useState } from "react";
import { useRole } from "../../lib/role.jsx";
import { getBreakdown, WC_ROLES, days } from "../../lib/workcapApi";
import { peso } from "../../lib/forecastApi";
import { cn } from "../../lib/utils";

const BY = [["branch", "Branch"], ["category", "Category"], ["class", "ABC × XYZ"], ["sku", "SKU"]];
const btn = (on) => cn("h-7 px-2.5 rounded-sm text-[11.5px] border transition-colors", on ? "border-primary text-primary bg-primary/10" : "border-border text-muted hover:text-fg");

const FULL = [["Name", r => r.name], ["DIO", r => days(r.dio)], ["Same month LY", r => days(r.dio_same_month_last_year), "text-muted"], ["CCC", r => days(r.ccc)],
  ["GMROI", r => r.gmroi?.toFixed(2) ?? "—"], ["Inventory", r => peso(r.inventory_php)], ["Cash tied", r => peso(r.cash_tied_php)]];
const COMPACT = [FULL[0], FULL[4], FULL[1], FULL[6]];

function Table({ rows, testid, cols = FULL }) {
  return (
    <div className="overflow-x-auto max-h-[420px] overflow-y-auto" data-testid={testid}>
      <table className="w-full text-[12px]">
        <thead className="sticky top-0 bg-surface"><tr className="text-[10px] uppercase tracking-widest text-muted">
          {cols.map(([h], i) => <th key={h} className={cn("py-2 px-3 border-b border-border whitespace-nowrap", i ? "text-right" : "text-left")}>{h}</th>)}</tr></thead>
        <tbody className="font-mono-tabular">{rows.map(r => (
          <tr key={r.name} className="border-b border-border/60">
            {cols.map(([h, f, c], i) => <td key={h} className={cn("py-2 px-3", i ? "text-right whitespace-nowrap" : "font-sans truncate max-w-[220px]", c)}>{f(r)}</td>)}</tr>))}</tbody>
      </table>
    </div>
  );
}

// Inventory Intelligence: DIO, CCC and GMROI by branch, category, class or SKU (CEO, CFO, COO only).
export default function WorkingCapitalBreakdown() {
  const { role } = useRole();
  const [by, setBy] = useState("branch");
  const [win, setWin] = useState(90);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const allowed = WC_ROLES.includes(role?.id);
  useEffect(() => { if (allowed) { setErr(null); getBreakdown(by, win).then(setD).catch(e => setErr(e.message)); } }, [allowed, by, win]);
  if (!allowed) return null;
  return (
    <div className="tile" data-testid="wc-breakdown">
      <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2">
        <div className="kpi-label flex-1 min-w-[160px]">Working capital by…</div>
        {BY.map(([k, l]) => <button key={k} data-testid={`wc-by-${k}`} onClick={() => setBy(k)} className={btn(by === k)}>{l}</button>)}
        <span className="w-px h-5 bg-border mx-1" />
        {[30, 90, 365].map(w => <button key={w} data-testid={`wc-window-${w}`} onClick={() => setWin(w)} className={btn(win === w)}>{w} d</button>)}
      </div>
      {err && <div className="p-4 text-critical text-[12px]" data-testid="wc-breakdown-error">{err}</div>}
      {!d ? !err && <div className="p-4 text-sm text-muted">Loading…</div> : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-0 xl:divide-x divide-border">
          <div className="xl:col-span-2"><div className="px-4 pt-3 text-[11px] text-muted">Ranked by cash tied up (DIO × inventory value) · DIO over {d.window} days · same month last year shows the seasonal norm</div>
            <div className="px-4 pt-1 text-[11px] text-subtle" data-testid="wc-breakdown-scope">{d.scope}{d.window_note ? ` · ${d.window_note}` : ""}</div>
            <Table rows={d.rows} testid="wc-breakdown-table" /></div>
          <div><div className="px-4 pt-3 text-[11px] text-muted">Lowest GMROI — candidates for drop-ship or central pooling at a regional hub</div>
            <Table rows={d.lowest_gmroi} testid="wc-low-gmroi" cols={COMPACT} /></div>
        </div>)}
    </div>
  );
}
